/* =============================================================
   dd-countdown.js

   Live "Order within H hours and M minutes…" countdown used by:
     • PDP urgency block (sections/main-product.liquid, dd_urgency)
     • Header announcement bar's "countdown" block (sections/
       announcement-bar.liquid, block type `countdown`), which
       also gets cloned into the glass-nav ticker's marquee.

   Responsibility split (see assets/dd-countdown-boot.js):
     - First-paint initial values are owned by the EAGER boot script
       (`dd-countdown-boot.js`). It runs at DOMContentLoaded ahead
       of the deferred queue and writes correct H/M into every
       `[data-dd-countdown]` wrapper before the user's eye lands on
       the slot.
     - This file (DEFERRED) handles the long-running concerns only:
         · 60s `setInterval` ticker that keeps the slots fresh while
           the shopper lingers on the page.
         · `MutationObserver` on the glass-nav announcement ticker
           that detects cloned countdown markup and ticks it.
         · Idempotent re-invocation guard (`window._ddCountdownInit`).

     There is intentionally NO startup `tickAll()` call here anymore.
     The boot script has already set initial values; the first work
     this file does is the first interval tick, ~60s after load.
     `buildUpdater` closures are built lazily on that tick.

   Design notes:
     - Multi-instance: any number of `[data-dd-countdown]` wrappers
       can coexist on a page, each with its own cutoff config
       (`data-cutoff-hour/minute/tz`). The ticker duplicates the
       countdown markup into many <span> clones as part of the
       marquee; each clone is a full wrapper + slots, so scoping
       updates per wrapper keeps things clean regardless of how
       many copies are on the page.
     - Scoped slot updates: hours/minutes slots are updated *within*
       each wrapper, not globally. Stops a mismatched PDP + header
       cutoff config from clobbering each other.
     - Observer hook: the glass-nav ticker builds its marquee from
       `.announcement-bar__message` innerHTML on a frame after this
       IIFE runs. A MutationObserver on the ticker container picks
       up any new `[data-dd-countdown]` wrappers that get injected
       later. Cloned wrappers don't carry `data-dd-countdown-ready`
       (it gets stripped in some clone paths or never set if the
       boot script ran before the clone existed) — the update
       closure here re-asserts the ready flag every tick so the
       wrapper's visibility cushion always lifts as soon as we're
       managing it.
   ============================================================= */

if (window._ddCountdownInit) {
  // No-op: script already running. Avoids duplicate intervals if
  // the file is loaded twice (e.g. editor reloads, legacy PDP tag).
} else {
  window._ddCountdownInit = true;

  (function () {
    'use strict';
    var _isTicking = false;
    var _observerTickTimer = null;
    var _intervalTimer = null;

    function readParts(formatter, date) {
      var parts = formatter.formatToParts(date);
      var out = {};
      for (var i = 0; i < parts.length; i++) {
        out[parts[i].type] = parts[i].value;
      }
      return out;
    }

    /* Holiday + Sunday awareness comes from `window.DDShippingCalendar`,
       defined inline in layout/theme.liquid's <head>. Same global is
       shared with dd-countdown-boot.js and the cart/cart-page inline
       IIFEs — single edit point for adding new holidays.
       Defensive fallback if the global is missing (asset cached
       through a layout deploy gap, or test harness that swaps theme
       layout): degrade gracefully to a Sunday-only skip rather than
       throwing. The user gets a slightly-too-optimistic cutoff for
       the missing-global window rather than a stuck `--` slot. */
    function getNextCutoffMs(cutoffHour, cutoffMinute, tz) {
      var cal = window.DDShippingCalendar;
      if (cal && typeof cal.getMsUntilNextCutoff === 'function') {
        return cal.getMsUntilNextCutoff(cutoffHour, cutoffMinute, tz);
      }
      return NaN;
    }

    // Build an updater closure for one `[data-dd-countdown]` wrapper.
    // Returns null if the config is invalid (unparseable tz, missing
    // cutoff hour, etc.) so the caller can skip it quietly.
    function buildUpdater(root) {
      var cutoffHour = parseInt(root.getAttribute('data-cutoff-hour'), 10);
      var cutoffMinute = parseInt(root.getAttribute('data-cutoff-minute'), 10) || 0;
      var tz = root.getAttribute('data-cutoff-tz') || 'America/Phoenix';
      if (isNaN(cutoffHour)) return null;

      var formatter;
      try {
        formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: tz,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        });
      } catch (err) {
        return null;
      }

      // `data-dd-countdown-managed` opts an element out of our
      // automatic `hidden` toggles. The PDP urgency block sets this
      // because its visibility is driven by variant state (in-stock /
      // custom / awaiting-restock) — three mutually-exclusive <p>s
      // share the same slot and the urgency manager owns which one is
      // `hidden`. Without the opt-out, this updater would flip
      // `hidden = false` on the countdown paragraph every tick and it
      // would show alongside the "Out of stock" nudge or "Custom 6-8
      // wk" message. Slot text still updates either way so the
      // countdown stays fresh if/when the shopper switches back to
      // an in-stock variant.
      var managed = root.hasAttribute('data-dd-countdown-managed');

      return function update() {
        // Re-assert `data-dd-countdown-ready` every tick. The boot
        // script (dd-countdown-boot.js) sets it once at first paint
        // for wrappers present at DCL; this catches the glass-nav
        // ticker clones that get added afterwards and any markup
        // re-rendered by Shopify's Section Rendering API. The
        // companion CSS rule injected by the boot script keeps a
        // wrapper invisible until this attribute exists.
        if (!root.hasAttribute('data-dd-countdown-ready')) {
          root.setAttribute('data-dd-countdown-ready', '');
        }

        var ms = getNextCutoffMs(cutoffHour, cutoffMinute, tz);
        if (isNaN(ms) || ms < 0) {
          // Hide the whole message rather than leaving the `--`
          // placeholders stranded. Skip when the element is
          // externally managed — caller decides visibility.
          if (!managed) root.hidden = true;
          return;
        }
        if (!managed) root.hidden = false;

        var totalMin = Math.floor(ms / 60000);
        var h = String(Math.floor(totalMin / 60));
        var m = String(totalMin % 60);

        // All slots inside THIS root only — scoping matters for
        // multi-instance pages (PDP + header) with different configs.
        //
        // Gate every write behind an equality check. Setting
        // `textContent` always replaces the underlying text node, which
        // fires `childList` MutationRecords on the slot's parent even
        // when the visible value is unchanged. Slots inside the
        // glass-nav ticker marquee live under a `MutationObserver` that
        // re-invokes this updater (see attachObserver), so an
        // unconditional write here creates a self-triggering microtask
        // storm that freezes the main thread on the first
        // `setInterval` tick (~60s after load). Skipping no-op writes
        // breaks that cycle and also saves DOM work on the common case
        // where hours/minutes haven't moved.
        var hoursEls = root.querySelectorAll('[data-dd-countdown-hours]');
        var minsEls = root.querySelectorAll('[data-dd-countdown-minutes]');
        for (var i = 0; i < hoursEls.length; i++) {
          if (hoursEls[i].textContent !== h) hoursEls[i].textContent = h;
        }
        for (var j = 0; j < minsEls.length; j++) {
          if (minsEls[j].textContent !== m) minsEls[j].textContent = m;
        }
      };
    }

    function tickAll() {
      if (_isTicking) return;
      _isTicking = true;
      var roots = document.querySelectorAll('[data-dd-countdown]');
      try {
        for (var i = 0; i < roots.length; i++) {
          var root = roots[i];
          if (!root._ddCountdownUpdate) {
            var updater = buildUpdater(root);
            if (!updater) continue;
            root._ddCountdownUpdate = updater;
          }
          root._ddCountdownUpdate();
        }
      } finally {
        _isTicking = false;
      }
    }

    function scheduleIntervalTick() {
      clearTimeout(_intervalTimer);
      _intervalTimer = setTimeout(function () {
        tickAll();
        scheduleIntervalTick();
      }, 60000);
    }

    function scheduleObserverTick() {
      if (_observerTickTimer) return;
      _observerTickTimer = setTimeout(function () {
        _observerTickTimer = null;
        tickAll();
      }, 50);
    }

    // No initial `tickAll()` here — `dd-countdown-boot.js` (loaded
    // EAGER in <head> from layout/theme.liquid) has already written
    // initial H/M into every `[data-dd-countdown]` wrapper present
    // at first paint. Building the update closures here would just
    // re-do the same compute the boot script already did. Closures
    // get built lazily on the first interval tick (60s later) when
    // the slots actually need refreshing.
    //
    // Glass-nav clones that arrive AFTER boot get picked up by the
    // MutationObserver below, which calls `tickAll()` ~50ms after
    // the clone — that path builds their closures on demand.
    scheduleIntervalTick();

    // Ticker hook — glass-nav clones `.announcement-bar__message`
    // markup (including our `[data-dd-countdown]` wrapper) into
    // `.glass-nav-announcement` in a frame AFTER this IIFE runs.
    // Observe and re-scan so the fresh clones pick up their slots.
    //
    // IMPORTANT: only re-tick when a fresh `[data-dd-countdown]`
    // wrapper is actually injected. `tickAll` itself writes
    // `textContent` on slots that live inside this subtree; reacting
    // to every mutation record (including our own text writes) causes
    // the observer to re-invoke tickAll in a tight microtask loop and
    // freeze the tab ~60s after load when the first setInterval fires.
    function hasNewCountdownRoot(records) {
      for (var r = 0; r < records.length; r++) {
        var added = records[r].addedNodes;
        if (!added || !added.length) continue;
        for (var n = 0; n < added.length; n++) {
          var node = added[n];
          if (node.nodeType !== 1) continue;
          if (node.hasAttribute && node.hasAttribute('data-dd-countdown')) return true;
          if (node.querySelector && node.querySelector('[data-dd-countdown]')) return true;
        }
      }
      return false;
    }

    function attachObserver() {
      var tickerEl = document.querySelector('.glass-nav-announcement');
      if (!tickerEl || tickerEl._ddCountdownObserver) return;
      try {
        var obs = new MutationObserver(function (records) {
          if (hasNewCountdownRoot(records)) scheduleObserverTick();
        });
        obs.observe(tickerEl, { childList: true, subtree: true });
        tickerEl._ddCountdownObserver = obs;
      } catch (e) {}
    }

    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', attachObserver);
    } else {
      attachObserver();
    }
  })();
}
